import { Icon } from '@iconify/react/dist/iconify.js';
import { useState, useMemo } from 'react';
import { PaginationLimit } from '../../../../components/PaginationLimit';
import SearchableDropdown from '../../../../components/SearchableDropdown';
import ViewReasonModal from './ViewReasonModal';
import ApproveConfirmModal from './ApproveConfirmModal';
import RejectModal from './RejectModal';
import DeleteConfirmModal from './DeleteConfirmModal';
import BulkApproveModal from './BulkApproveModal';
import BulkRejectModal from './BulkRejectModal';
import BulkDeleteModal from './BulkDeleteModal';

// Sample data — replace with actual API data
const SAMPLE_REQUESTS = [
    { id: 1, societyId: '4240', societyName: 'Zipgrid Family', credits: 1000, amount: 350, requestDate: '04 Feb 2025, 06:41 PM', status: 'Rejected', rejectionReason: 'Invalid payment details provided' },
    { id: 2, societyId: '4176', societyName: 'IFCI', credits: 12500, amount: 3500, requestDate: '04 Feb 2025, 06:31 PM', status: 'Rejected', rejectionReason: 'Duplicate request' },
    { id: 3, societyId: '4176', societyName: 'IFCI', credits: 5600, amount: 1800, requestDate: '04 Feb 2025, 04:59 PM', status: 'Approved' },
    { id: 4, societyId: '4176', societyName: 'IFCI', credits: 12500, amount: 3500, requestDate: '04 Feb 2025, 04:57 PM', status: 'Approved' },
    { id: 5, societyId: '4240', societyName: 'Zipgrid Family', credits: 12500, amount: 3500, requestDate: '04 Feb 2025, 04:50 PM', status: 'Rejected', rejectionReason: 'Payment not received' },
    { id: 6, societyId: '4175', societyName: 'Haunted Societyyew', credits: 5600, amount: 1800, requestDate: '30 Jan 2025, 05:42 PM', status: 'Pending' },
    { id: 7, societyId: '4175', societyName: 'Haunted Societyyew', credits: 1000, amount: 350, requestDate: '27 Nov 2024, 08:01 PM', status: 'Pending' },
    { id: 8, societyId: '4175', societyName: 'Haunted Societyyew', credits: 1000, amount: 350, requestDate: '03 May 2024, 06:10 PM', status: 'Pending' },
];

const STATUS_BADGE = {
    Pending: { bg: 'tw-bg-amber-50', text: 'tw-text-amber-600', border: 'tw-border-amber-100', dot: 'tw-bg-amber-500' },
    Approved: { bg: 'tw-bg-emerald-50', text: 'tw-text-emerald-600', border: 'tw-border-emerald-100', dot: 'tw-bg-emerald-500' },
    Rejected: { bg: 'tw-bg-red-50', text: 'tw-text-red-500', border: 'tw-border-red-100', dot: 'tw-bg-red-500' },
};

const SmsCreditRequestListLayer = () => {
    const [communityFilter, setCommunityFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [selectedIds, setSelectedIds] = useState([]);

    // Modals
    const [viewReasonModalOpen, setViewReasonModalOpen] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [approveModalOpen, setApproveModalOpen] = useState(false);
    const [requestToApprove, setRequestToApprove] = useState(null);
    const [rejectModalOpen, setRejectModalOpen] = useState(false);
    const [requestToReject, setRequestToReject] = useState(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [requestToDelete, setRequestToDelete] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [bulkApproveModalOpen, setBulkApproveModalOpen] = useState(false);
    const [bulkRejectModalOpen, setBulkRejectModalOpen] = useState(false);
    const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);

    const filteredRequests = useMemo(() => {
        return SAMPLE_REQUESTS.filter((r) => {
            if (communityFilter && r.societyId !== communityFilter) return false;
            if (statusFilter !== 'All' && r.status !== statusFilter) return false;
            return true;
        });
    }, [communityFilter, statusFilter]);

    const selectedRequests = useMemo(
        () => filteredRequests.filter((req) => selectedIds.includes(req.id)),
        [filteredRequests, selectedIds]
    );
    const selectedPendingRequests = useMemo(
        () => selectedRequests.filter((req) => req.status === 'Pending'),
        [selectedRequests]
    );

    const isAllSelected = filteredRequests.length > 0 && filteredRequests.every((r) => selectedIds.includes(r.id));
    const isIndeterminate = selectedIds.length > 0 && !isAllSelected;

    const handleSelectAll = (e) => {
        if (e.target.checked) setSelectedIds(filteredRequests.map((r) => r.id));
        else setSelectedIds([]);
    };
    const handleSelectRow = (id) => {
        setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
    };
    const clearSelection = () => setSelectedIds([]);

    const handleConfirmApprove = async () => {
        setIsLoading(true);
        await new Promise((r) => setTimeout(r, 600));
        setApproveModalOpen(false);
        setRequestToApprove(null);
        setIsLoading(false);
    };
    const handleConfirmReject = async () => {
        setIsLoading(true);
        await new Promise((r) => setTimeout(r, 600));
        setRejectModalOpen(false);
        setRequestToReject(null);
        setIsLoading(false);
    };
    const handleConfirmDelete = async () => {
        setIsLoading(true);
        await new Promise((r) => setTimeout(r, 600));
        setDeleteModalOpen(false);
        setRequestToDelete(null);
        setIsLoading(false);
    };
    const handleConfirmBulkApprove = async () => {
        setIsLoading(true);
        await new Promise((r) => setTimeout(r, 600));
        setBulkApproveModalOpen(false);
        clearSelection();
        setIsLoading(false);
    };
    const handleConfirmBulkReject = async () => {
        setIsLoading(true);
        await new Promise((r) => setTimeout(r, 600));
        setBulkRejectModalOpen(false);
        clearSelection();
        setIsLoading(false);
    };
    const handleConfirmBulkDelete = async () => {
        setIsLoading(true);
        await new Promise((r) => setTimeout(r, 600));
        setBulkDeleteModalOpen(false);
        clearSelection();
        setIsLoading(false);
    };

    const StatusBadge = ({ status }) => {
        const cfg = STATUS_BADGE[status] || STATUS_BADGE.Pending;
        return (
            <span className={`tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-medium tw-border ${cfg.bg} ${cfg.text} ${cfg.border}`}>
                <span className={`tw-w-1.5 tw-h-1.5 tw-rounded-full ${cfg.dot}`}></span>
                {status}
            </span>
        );
    };

    return (
        <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-overflow-hidden">
            {/* Filters Bar */}
            <div className="tw-p-4 tw-border-b tw-border-gray-100 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                    <div className="tw-w-48">
                        <SearchableDropdown
                            value={communityFilter || 'all'}
                            onChange={(v) => setCommunityFilter(v === 'all' ? '' : v)}
                            options={[
                                { value: 'all', label: 'All Communities' },
                                { value: '4240', label: 'Zipgrid Family' },
                                { value: '4176', label: 'IFCI' },
                                { value: '4175', label: 'Haunted Societyyew' },
                            ]}
                            placeholder="Community"
                            clearable={false}
                            small
                        />
                    </div>
                    <div className="tw-w-40">
                        <SearchableDropdown
                            value={statusFilter}
                            onChange={setStatusFilter}
                            options={[
                                { value: 'All', label: 'All Status' },
                                { value: 'Pending', label: 'Pending' },
                                { value: 'Approved', label: 'Approved' },
                                { value: 'Rejected', label: 'Rejected' },
                            ]}
                            placeholder="Status"
                            clearable={false}
                            small
                        />
                    </div>
                </div>
                <button
                    className="tw-h-8 tw-w-8 tw-inline-flex tw-items-center tw-justify-center tw-rounded-md tw-text-gray-500 tw-bg-white tw-border tw-border-gray-200 hover:tw-bg-gray-50"
                    title="Refresh"
                >
                    <Icon icon="mdi:refresh" className="tw-text-base" />
                </button>
            </div>

            {/* Bulk Actions Bar */}
            {selectedIds.length > 0 && (
                <div className="tw-bg-rose-50/60 tw-border-b tw-border-rose-100 tw-px-4 tw-py-2 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
                    <div className="tw-flex tw-items-center tw-gap-3 tw-text-xs">
                        <span className="tw-font-medium tw-text-rose-700">
                            {selectedIds.length} item{selectedIds.length > 1 ? 's' : ''} selected
                        </span>
                        <button onClick={clearSelection} className="tw-text-gray-500 hover:tw-text-gray-700 tw-underline">
                            Clear selection
                        </button>
                    </div>
                    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-1.5">
                        {selectedPendingRequests.length > 0 && (
                            <button
                                onClick={() => setBulkApproveModalOpen(true)}
                                className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2.5 tw-py-1 tw-rounded tw-text-[11px] tw-font-medium tw-text-white tw-bg-emerald-600 hover:tw-bg-emerald-700"
                            >
                                <Icon icon="mdi:check-circle-outline" className="tw-text-sm" />
                                Approve ({selectedPendingRequests.length})
                            </button>
                        )}
                        {selectedPendingRequests.length > 0 && (
                            <button
                                onClick={() => setBulkRejectModalOpen(true)}
                                className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2.5 tw-py-1 tw-rounded tw-text-[11px] tw-font-medium tw-text-white tw-bg-amber-600 hover:tw-bg-amber-700"
                            >
                                <Icon icon="mdi:close-circle-outline" className="tw-text-sm" />
                                Reject ({selectedPendingRequests.length})
                            </button>
                        )}
                        <button
                            onClick={() => setBulkDeleteModalOpen(true)}
                            className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2.5 tw-py-1 tw-rounded tw-text-[11px] tw-font-medium tw-text-white tw-bg-red-600 hover:tw-bg-red-700"
                        >
                            <Icon icon="mingcute:delete-2-line" className="tw-text-sm" />
                            Delete ({selectedIds.length})
                        </button>
                    </div>
                </div>
            )}

            {/* Table */}
            <div className="tw-overflow-x-auto">
                <table className="tw-w-full tw-text-xs">
                    <thead>
                        <tr className="tw-bg-gradient-to-r tw-from-slate-50 tw-to-gray-50">
                            <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">
                                <span className="tw-inline-flex tw-items-center tw-gap-2">
                                    <input
                                        type="checkbox"
                                        checked={isAllSelected}
                                        ref={(el) => { if (el) el.indeterminate = isIndeterminate; }}
                                        onChange={handleSelectAll}
                                        className="tw-w-3.5 tw-h-3.5 tw-cursor-pointer"
                                    />
                                    Sl No.
                                </span>
                            </th>
                            <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">Community ID</th>
                            <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">Community Name</th>
                            <th className="tw-text-right tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">Credits</th>
                            <th className="tw-text-right tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">Amount</th>
                            <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">Request Date</th>
                            <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">Status</th>
                            <th className="tw-text-center tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="tw-divide-y tw-divide-gray-50">
                        {filteredRequests.length > 0 ? filteredRequests.map((request, index) => (
                            <tr
                                key={request.id}
                                className={`hover:tw-bg-slate-50/50 tw-transition-colors ${selectedIds.includes(request.id) ? 'tw-bg-rose-50/40' : ''}`}
                            >
                                <td className="tw-px-4 tw-py-2.5">
                                    <span className="tw-inline-flex tw-items-center tw-gap-2">
                                        <input
                                            type="checkbox"
                                            checked={selectedIds.includes(request.id)}
                                            onChange={() => handleSelectRow(request.id)}
                                            className="tw-w-3.5 tw-h-3.5 tw-cursor-pointer"
                                        />
                                        <span className="tw-text-gray-400">{String(index + 1).padStart(2, '0')}</span>
                                    </span>
                                </td>
                                <td className="tw-px-4 tw-py-2.5 tw-text-rose-600 tw-font-medium tw-font-mono">{request.societyId}</td>
                                <td className="tw-px-4 tw-py-2.5 tw-font-medium tw-text-gray-800">{request.societyName}</td>
                                <td className="tw-px-4 tw-py-2.5 tw-text-right">
                                    <span className="tw-inline-block tw-px-2 tw-py-0.5 tw-rounded tw-bg-blue-50 tw-text-blue-600 tw-text-[10px] tw-font-semibold">
                                        {request.credits.toLocaleString()}
                                    </span>
                                </td>
                                <td className="tw-px-4 tw-py-2.5 tw-text-right tw-font-medium tw-text-gray-700">₹{request.amount.toLocaleString()}</td>
                                <td className="tw-px-4 tw-py-2.5 tw-text-gray-500 tw-whitespace-nowrap">{request.requestDate}</td>
                                <td className="tw-px-4 tw-py-2.5"><StatusBadge status={request.status} /></td>
                                <td className="tw-px-4 tw-py-2.5">
                                    <div className="tw-flex tw-items-center tw-justify-center tw-gap-1">
                                        {request.status === 'Pending' && (
                                            <>
                                                <button
                                                    onClick={() => { setRequestToApprove(request); setApproveModalOpen(true); }}
                                                    className="tw-p-1 tw-rounded tw-bg-emerald-50 tw-text-emerald-600 hover:tw-bg-emerald-100 tw-transition-colors"
                                                    title="Approve"
                                                >
                                                    <Icon icon="mdi:check-circle-outline" className="tw-text-sm" />
                                                </button>
                                                <button
                                                    onClick={() => { setRequestToReject(request); setRejectModalOpen(true); }}
                                                    className="tw-p-1 tw-rounded tw-bg-amber-50 tw-text-amber-600 hover:tw-bg-amber-100 tw-transition-colors"
                                                    title="Reject"
                                                >
                                                    <Icon icon="mdi:close-circle-outline" className="tw-text-sm" />
                                                </button>
                                            </>
                                        )}
                                        {request.status === 'Rejected' && (
                                            <button
                                                onClick={() => { setSelectedRequest(request); setViewReasonModalOpen(true); }}
                                                className="tw-p-1 tw-rounded tw-bg-blue-50 tw-text-blue-600 hover:tw-bg-blue-100 tw-transition-colors"
                                                title="View Reason"
                                            >
                                                <Icon icon="mdi:information-outline" className="tw-text-sm" />
                                            </button>
                                        )}
                                        <button
                                            onClick={() => { setRequestToDelete(request); setDeleteModalOpen(true); }}
                                            className="tw-p-1 tw-rounded tw-bg-red-50 tw-text-red-500 hover:tw-bg-red-100 tw-transition-colors"
                                            title="Delete"
                                        >
                                            <Icon icon="mingcute:delete-2-line" className="tw-text-sm" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        )) : (
                            <tr>
                                <td colSpan={8} className="tw-text-center tw-py-12">
                                    <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
                                        <Icon icon="mdi:message-text-outline" className="tw-text-3xl tw-text-gray-300" />
                                        <p className="tw-text-xs tw-text-gray-400">No SMS credit requests found</p>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {filteredRequests.length > 0 && (
                <div className="tw-border-t tw-border-gray-100 tw-px-3 tw-py-2">
                    <PaginationLimit />
                </div>
            )}

            {/* Modals */}
            <ViewReasonModal
                open={viewReasonModalOpen}
                handleClose={() => { setViewReasonModalOpen(false); setSelectedRequest(null); }}
                request={selectedRequest}
            />
            <ApproveConfirmModal
                open={approveModalOpen}
                handleClose={() => { setApproveModalOpen(false); setRequestToApprove(null); }}
                handleConfirm={handleConfirmApprove}
                request={requestToApprove}
                isLoading={isLoading}
            />
            <RejectModal
                open={rejectModalOpen}
                handleClose={() => { setRejectModalOpen(false); setRequestToReject(null); }}
                handleConfirm={handleConfirmReject}
                request={requestToReject}
                isLoading={isLoading}
            />
            <DeleteConfirmModal
                open={deleteModalOpen}
                handleClose={() => { setDeleteModalOpen(false); setRequestToDelete(null); }}
                handleConfirm={handleConfirmDelete}
                title="Delete SMS Credit Request"
                itemName={requestToDelete?.societyName}
                itemId={requestToDelete?.id}
                isLoading={isLoading}
            />
            <BulkApproveModal
                open={bulkApproveModalOpen}
                handleClose={() => setBulkApproveModalOpen(false)}
                handleConfirm={handleConfirmBulkApprove}
                requests={selectedPendingRequests}
                isLoading={isLoading}
            />
            <BulkRejectModal
                open={bulkRejectModalOpen}
                handleClose={() => setBulkRejectModalOpen(false)}
                handleConfirm={handleConfirmBulkReject}
                requests={selectedPendingRequests}
                isLoading={isLoading}
            />
            <BulkDeleteModal
                open={bulkDeleteModalOpen}
                handleClose={() => setBulkDeleteModalOpen(false)}
                handleConfirm={handleConfirmBulkDelete}
                requests={selectedRequests}
                isLoading={isLoading}
            />
        </div>
    );
};

export default SmsCreditRequestListLayer;
